import React, { useState, useEffect, useCallback } from 'react';
// merged: incoming + existing
import Skeleton from '../common/Skeleton';
import { Search, Plus, Save, Check, Trash2, Settings, CircleMinus, CircleCheck, TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle, Clock, Lock } from 'lucide-react';
import controlRegisterService, { ControlRegister, ControlCategory } from '../../services/controlRegisterService';
import ControlConfigModal from './ControlConfigModal';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Badge, { BadgeVariant } from '../common/Badge';
import { statusVariant } from '../../utils/statusVariant';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, confirmActive, confirmDelete } from '../common/confirm';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import ApprovalDot, { WorkflowState } from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import WorkflowSaveBar, { SaveTarget } from '../common/WorkflowSaveBar';
import { usePermissions, usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { isUniqueLocal } from '../../validation/uniqueness';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

const ACT_CONTROL_LIBRARY = 'CTR_CONTROL_LIBRARY';
const WF_KIND = 'ctr_register';

const CONTROL_TYPE_OPTIONS = [
  { value: 'Preventive', label: 'Preventive' },
  { value: 'Detective', label: 'Detective' },
  { value: 'Corrective', label: 'Corrective' },
];

const SIGNAL_STYLE: Record<string, { variant: BadgeVariant; icon: React.ReactNode }> = {
  HEALTHY: { variant: 'success', icon: <CheckCircle   size={12} /> },
  WARNING: { variant: 'warning', icon: <AlertTriangle size={12} /> },
  BREACH:  { variant: 'danger',  icon: <AlertTriangle size={12} /> },
  UNKNOWN: { variant: 'neutral', icon: <Clock         size={12} /> },
};

const TREND_ICON: Record<string, React.ReactNode> = {
  UP:      <TrendingUp   size={14} className="text-green-500" />,
  DOWN:    <TrendingDown size={14} className="text-red-500" />,
  STABLE:  <Minus        size={14} className="text-gray-400" />,
  UNKNOWN: <Minus        size={14} className="text-gray-300" />,
};

const ControlRegisterTab: React.FC = () => {
  const { formatDate } = useEntitySettings();
  const fieldVh = useFieldValidation();
  const fieldVhReject = useFieldValidation();
  const [controls, setControls] = useState<ControlRegister[]>([]);
  const [categories, setCategories] = useState<ControlCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedControl, setSelectedControl] = useState<ControlRegister | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<Partial<ControlRegister>>({});
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [configControl, setConfigControl] = useState<ControlRegister | null>(null);
  const [workflowRow, setWorkflowRow] = useState<ControlRegister | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const { check } = usePermissions();
  const { allowed: canView, loading: viewLoading } = usePermission(ACT_CONTROL_LIBRARY, 'view');
  const { allowed: canModify, loading: permLoading } = usePermission(ACT_CONTROL_LIBRARY, 'view+modify');
  const canApproveExtent = check(ACT_CONTROL_LIBRARY, 'view+modify+approve').allowed;
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [submittingForApproval, setSubmittingForApproval] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const loadControls = useCallback(async () => {
    try {
      setLoading(true);
      const data = await controlRegisterService.getAll({
        status: statusFilter as any,
        search: searchTerm || undefined,
      });
      setControls(data);
    } catch (err: any) {
      // 403 = caller lacks view extent on this activity; render the empty
      // state silently instead of surfacing an RBAC denial as an error toast.
      if (err?.response?.status === 403 || err?.status === 403) {
        setControls([]);
      } else {
        setToast({ type: 'error', message: err.message || 'Failed to load controls' });
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  const loadCategories = async () => {
    try {
      const data = await controlRegisterService.getActiveCategories();
      setCategories(data);
    } catch (err: any) {
      console.error('Error loading categories:', err);
    }
  };

  useEffect(() => {
    if (canView) loadControls();
  }, [loadControls, canView]);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, pageSize]);

  useEffect(() => {
    if (!showModal) { fieldVh.reset(); return; }
    setSubmitting(false);
    setDeleting(false);
    setToggling(false);
    setSubmittingForApproval(false);
    setApproving(false);
  }, [showModal, selectedControl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showRejectionModal) { fieldVhReject.reset(); setRejecting(false); }
  }, [showRejectionModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRowClick = (control: ControlRegister) => {
    if (permLoading || !canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
    setConfigControl(control);
  };

  const handleAddNew = () => {
    setSelectedControl(null);
    setFormData({ control_code: '', control_name: '', control_description: '', category_id: undefined, control_type: undefined });
    setIsEditMode(false);
    fieldVh.reset();
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditMode && selectedControl && controlRegisterService.canEdit(selectedControl)) {
      const allValid = fieldVh.validateAll([
        { name: 'control_name', code: 109, value: formData.control_name || '', required: true },
        { name: 'control_description', code: 110, value: formData.control_description || '' },
      ]);
      if (!allValid) return;
    }
    const validation = controlRegisterService.validateData(formData, isEditMode);
    if (!validation.valid) {
      setToast({ type: 'error', message: 'Validation errors: ' + validation.errors.join('; ') });
      return;
    }
    if (isEditMode && selectedControl && !controlRegisterService.canEdit(selectedControl)) {
      setToast({ type: 'error', message: `Cannot edit control with status ${selectedControl.status}. Only DRAFT and REJECTED controls can be edited.` });
      return;
    }
    setSubmitting(true);
    try {
      if (isEditMode && selectedControl) {
        await controlRegisterService.update(selectedControl.id, formData);
        setToast({ type: 'success', message: 'Control updated successfully.' });
      } else {
        await controlRegisterService.create(formData);
        setToast({ type: 'success', message: 'Control created successfully.' });
      }
      setShowModal(false);
      loadControls();
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Operation failed' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateSave = async (target: SaveTarget) => {
    const allValid = fieldVh.validateAll([
      { name: 'control_name', code: 109, value: formData.control_name || '', required: true },
      { name: 'control_description', code: 110, value: formData.control_description || '' },
    ]);
    if (!allValid) return;
    const validation = controlRegisterService.validateData(formData, false);
    if (!validation.valid) {
      setToast({ type: 'error', message: 'Validation errors: ' + validation.errors.join('; ') });
      return;
    }
    setSubmitting(true);
    try {
      await controlRegisterService.create(formData, target);
      const msg = { draft: 'Control saved as draft.', submit: 'Control submitted for approval.', approve: 'Control created and approved.' }[target];
      setShowModal(false);
      setToast({ type: 'success', message: msg });
      loadControls();
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Create failed' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!selectedControl) return;
    if (!controlRegisterService.canSubmit(selectedControl)) {
      setToast({ type: 'error', message: `Cannot submit control with status ${selectedControl.status}. Only DRAFT and REJECTED controls can be submitted.` });
      return;
    }
    const ok = await confirmDelete({
      title: 'Submit for approval?',
      text: `"${selectedControl.control_name}" will be sent for approval.`,
      confirmText: 'Yes, Submit',
    });
    if (!ok) return;
    setSubmittingForApproval(true);
    try {
      await controlRegisterService.submit(selectedControl.id);
      setShowModal(false);
      setToast({ type: 'success', message: 'Submitted for approval.' });
      loadControls();
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to submit for approval' });
    } finally {
      setSubmittingForApproval(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedControl) return;
    if (selectedControl.status !== 'PENDING_APPROVAL') {
      setToast({ type: 'error', message: 'Only PENDING_APPROVAL controls can be approved' });
      return;
    }
    const ok = await confirmDelete({
      title: 'Approve this control?',
      text: `"${selectedControl.control_name}" will be approved and become available.`,
      confirmText: 'Yes, Approve',
    });
    if (!ok) return;
    setApproving(true);
    try {
      await controlRegisterService.approve(selectedControl.id);
      setShowModal(false);
      setToast({ type: 'success', message: 'Control approved.' });
      loadControls();
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to approve' });
    } finally {
      setApproving(false);
    }
  };

  const handleRejectClick = () => {
    if (!selectedControl) return;
    if (selectedControl.status !== 'PENDING_APPROVAL') {
      setToast({ type: 'error', message: 'Only PENDING_APPROVAL controls can be rejected' });
      return;
    }
    setRejectionReason('');
    setShowRejectionModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedControl) return;
    const rejectValid = fieldVhReject.validateAll([
      { name: 'rejection_reason', code: 110, value: rejectionReason, required: true },
    ]);
    if (!rejectValid) return;
    if (!rejectionReason || rejectionReason.trim() === '') {
      setToast({ type: 'error', message: 'Rejection reason is required' });
      return;
    }
    setRejecting(true);
    try {
      await controlRegisterService.reject(selectedControl.id, rejectionReason);
      setShowRejectionModal(false);
      setShowModal(false);
      setToast({ type: 'success', message: 'Control rejected.' });
      loadControls();
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to reject' });
    } finally {
      setRejecting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedControl) return;
    if (selectedControl.is_active === 1) {
      setToast({ type: 'error', message: 'Inactivate this control before deleting it.' });
      return;
    }
    if (!controlRegisterService.canDelete(selectedControl)) {
      setToast({ type: 'error', message: `Cannot delete control with status ${selectedControl.status}. Only DRAFT and REJECTED controls can be deleted.` });
      return;
    }
    const ok = await confirmDelete({
      title: 'Delete this control?',
      text: `"${selectedControl.control_name}" will be permanently removed. Audit history is preserved.`,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await controlRegisterService.delete(selectedControl.id);
      setShowModal(false);
      setToast({ type: 'success', message: 'Control deleted.' });
      loadControls();
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Delete failed' });
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedControl) return;
    if (!controlRegisterService.canToggle(selectedControl)) {
      setToast({ type: 'error', message: 'Only APPROVED controls can be activated/inactivated' });
      return;
    }
    const next = selectedControl.is_active === 1 ? 0 : 1;
    const isActive = selectedControl.is_active === 1;
    const confirmFn = isActive ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? 'Reactivate this control?' : 'Inactivate this control?',
      text: next
        ? 'It will appear in active lists again.'
        : 'It will be hidden from active lists.',
      confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
    });
    if (!ok) return;
    setToggling(true);
    try {
      await controlRegisterService.toggleStatus(selectedControl.id);
      setShowModal(false);
      setToast({ type: next ? 'success' : 'warning', message: next ? 'Reactivated' : 'Inactivated' });
      loadControls();
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to update status' });
    } finally {
      setToggling(false);
    }
  };

  const totalRecords = controls.length;
  const paginatedControls = controls.slice((page - 1) * pageSize, page * pageSize);

  const categoryOptions = categories.map(cat => ({
    value: cat.id,
    label: `${cat.category_name} (${cat.category_code})`,
  }));

  const canEdit = !!(isEditMode && selectedControl && controlRegisterService.canEdit(selectedControl));

  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to 'CTR_CONTROL_LIBRARY'." />
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full">

      <div className="netbody-header">
        {/* Active/Inactive availability — iOS switch. Shares the single `status`
            param with the workflow-state dropdown; whichever is touched last wins. */}
        <StatusSwitch
          value={statusFilter === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE'}
          onChange={next => setStatusFilter(next)}
          noun="controls"
          testId="SEL_control_register_status_filter"
        />
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.SearchSelect
            value={['ACTIVE', 'INACTIVE'].includes(statusFilter) ? 'ALL' : statusFilter}
            onChange={v => setStatusFilter(String(v ?? 'ALL'))}
            className="filter-status"
            data-testid="SEL_control_register_workflow_filter"
            noSearchIcon
            options={[
              { value: 'ALL', label: 'All Status' },
              { value: 'DRAFT', label: 'Draft' },
              { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
              { value: 'APPROVED', label: 'Approved' },
              { value: 'REJECTED', label: 'Rejected' },
            ]}
          />
          <FormControl.Input
            type="text"
            placeholder="Search controls…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
            data-testid="INP_control_register_search"
          />
          <Button
            variant="fill"
            icon={Plus}
            onClick={handleAddNew}
            disabled={permLoading || !canModify}
            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
            data-testid="BTN_control_register_create"
          >
            Create
          </Button>
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            {!loading && controls.length === 0 ? (
              <NoDataFound title="No Controls Found" />
            ) : (
            <>
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Code</th>
                    <th className="table-list-th">Control Name</th>
                    <th className="table-list-th">Description</th>
                    <th className="table-list-th">Category</th>
                    <th className="table-list-th">Type</th>
                    <th className="table-list-th">Risks</th>
                    <th className="table-list-th">Implementations</th>
                    <th className="table-list-th">Net Score</th>
                    <th className="table-list-th">Net Deficiency</th>
                    <th className="table-list-th">Trend</th>
                    <th className="table-list-th">Signal</th>
                    <th className="table-list-th">Last Check</th>
                  </tr>
                </thead>
                <tbody aria-busy={loading} aria-live="polite">
                  {loading && <Skeleton.TableRows cols={['dot', '3xs', 'xs', 'xs', 'md', 'md', 'sm', '2xs', '2xs', '2xs', '2xs', '2xs', 'pill', 'sm']} />}
                  {!loading && paginatedControls.map((control, idx) => (
                    <tr
                      key={control.id}
                      className="table-list-row"
                      onClick={() => handleRowClick(control)}
                      title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                      aria-disabled={permLoading || !canModify}
                      style={(permLoading || !canModify) ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                      data-testid={`BTN_control_register_row_${control.id}_open`}
                    >
                      <td
                        className="table-list-cell table-list-cell--dot"
                        onClick={(e) => { e.stopPropagation(); setWorkflowRow(control); }}
                        title="Workflow — view timeline & act"
                        data-testid={`BTN_control_register_row_${control.id}_workflow`}
                      >
                        <ApprovalDot
                          state={(((control as any).workflow_state) || 'draft') as WorkflowState}
                          onClick={() => setWorkflowRow(control)}
                          title="Workflow — view timeline & act"
                        />
                      </td>
                      <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                      <td className="table-list-cell">
                        {control.code
                          ? <span className="cell-code">{control.code}</span>
                          : <span className="text-muted text-xs">—</span>}
                      </td>
                      <td className="table-list-cell">
                        <span className="table-list-cell-title cell-name-lock">
                          {control.workflow_state === 'approved' && (
                            <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                          )}
                          {control.control_name}
                        </span>
                      </td>
                      <td className="table-list-cell table-list-cell--truncate" title={control.control_description || ''}>
                        {control.control_description || '—'}
                      </td>
                      <td className="table-list-cell">{control.category_name || '—'}</td>
                      <td className="table-list-cell">
                        {control.control_type ? (
                          <Badge variant="info" size="sm">{control.control_type}</Badge>
                        ) : '—'}
                      </td>
                      <td className="table-list-cell">
                        {(control.risk_count ?? 0) > 0 ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700" title={`${control.risk_count} risk(s) mitigated`}>
                            {control.risk_count}
                          </span>
                        ) : <span className="text-xs text-gray-400">0</span>}
                      </td>
                      <td className="table-list-cell">
                        {(control.implementation_count ?? 0) > 0 ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700" title={`${control.implementation_count} implementation(s) defined`}>
                            {control.implementation_count}
                          </span>
                        ) : <span className="text-xs text-gray-400">0</span>}
                      </td>
                      <td className="table-list-cell">
                        {control.display_net_score != null ? (
                          <span className="text-sm font-semibold text-gray-700">
                            {Number(control.display_net_score).toFixed(1)}
                            <span className="text-xs text-gray-400 ml-1">/ 25</span>
                          </span>
                        ) : '—'}
                      </td>
                      <td className="table-list-cell">
                        {control.net_deficiency != null ? (
                          <span className="text-sm font-semibold text-rose-700">
                            {Number(control.net_deficiency).toFixed(1)}
                            <span className="text-xs text-gray-400 ml-1">/ 25</span>
                          </span>
                        ) : '—'}
                      </td>
                      <td className="table-list-cell">
                        <span className="inline-flex items-center" title={control.trend || 'UNKNOWN'}>
                          {TREND_ICON[control.trend || 'UNKNOWN']}
                        </span>
                      </td>
                      <td className="table-list-cell">
                        {(() => {
                          const s = control.signal || 'UNKNOWN';
                          const cfg = SIGNAL_STYLE[s];
                          return (
                            <Badge variant={cfg.variant} size="sm" className="inline-flex items-center gap-1">
                              {cfg.icon}
                              {s}
                            </Badge>
                          );
                        })()}
                      </td>
                      <td className="table-list-cell">
                        <span className="text-xs text-gray-600">{formatDate(control.last_check)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              recordLabel="controls"
            />
            </>
          )}
          </div>
        </div>
      </div>

      {/* Main Modal — Add/Edit Control */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="lg">
        <Modal.Header
          actions={
            isEditMode && selectedControl && (
              <button
                type="button"
                className="btn-primary-outline-none app-modal-header-icon"
                onClick={() => setConfigControl(selectedControl)}
                disabled={permLoading || !canModify}
                title={!canModify ? NOT_AUTHORISED_MSG : 'Configure linkages (risks, procedures, implementations)'}
                data-testid="BTN_control_register_configure"
              >
                <Settings size={16} />
              </button>
            )
          }
        >
          {isEditMode && selectedControl
            ? <>Editing "<span className="item-highlight">{selectedControl.control_name}</span>"</>
            : 'Create Control'}
        </Modal.Header>
        <Modal.Body>
          {isEditMode && selectedControl && (
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Current Status</p>
              <Badge variant={statusVariant(selectedControl.workflow_state || 'draft')} size="md">
                {controlRegisterService.getWorkflowStatusText(selectedControl.workflow_state)}
              </Badge>
              {selectedControl.rejection_reason && (
                <div style={{ marginTop: '8px', padding: '8px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px' }}>
                  <strong style={{ color: '#dc2626' }}>Rejection Reason:</strong>
                  <p style={{ margin: '4px 0 0 0', color: '#7f1d1d' }}>{selectedControl.rejection_reason}</p>
                </div>
              )}
            </div>
          )}
          <form id="control-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4" data-testid="MOD_control_register_form">
            <ValidatedInput
              label="Control Name"
              required
              name="control_name"
              code={109}
              vh={fieldVh}
              value={formData.control_name || ''}
              onChange={(e) => setFormData({ ...formData, control_name: e.target.value })}
              onAfterBlur={(val) => {
                if (!val) return;
                const excludeId = isEditMode && selectedControl ? selectedControl.id : undefined;
                const unique = isUniqueLocal(943, val, controls, excludeId);
                if (!unique) fieldVh.setError('control_name', 'Control name already exists');
              }}
              placeholder="e.g., Access Control Procedure"
              disabled={isEditMode && !!selectedControl && !controlRegisterService.canEdit(selectedControl)}
              data-testid="INP_control_register_name"
            />
            <ValidatedTextarea
              label="Control Description"
              name="control_description"
              code={110}
              vh={fieldVh}
              value={formData.control_description || ''}
              onChange={(e) => setFormData({ ...formData, control_description: e.target.value })}
              rows={3}
              disabled={isEditMode && !!selectedControl && !controlRegisterService.canEdit(selectedControl)}
              data-testid="INP_control_register_description"
            />
            <FormControl.SearchSelect
              label="Control Category"
              required
              options={categoryOptions}
              value={formData.category_id ?? null}
              onChange={(v) => setFormData({ ...formData, category_id: v ? Number(v) : undefined })}
              placeholder="-- Select Category --"
              disabled={isEditMode && !!selectedControl && !controlRegisterService.canEdit(selectedControl)}
              data-testid="SEL_control_register_category"
            />
            <FormControl.SearchSelect
              label="Control Type"
              options={CONTROL_TYPE_OPTIONS}
              value={formData.control_type || null}
              onChange={(v) => setFormData({ ...formData, control_type: v ? String(v) : undefined })}
              placeholder="-- Select Type --"
              clearable
              disabled={isEditMode && !!selectedControl && !controlRegisterService.canEdit(selectedControl)}
              data-testid="SEL_control_register_type"
            />
          </form>
        </Modal.Body>
        <Modal.Footer
          className={
            isEditMode && selectedControl && ((selectedControl.is_active !== 1 && controlRegisterService.canDelete(selectedControl)) || controlRegisterService.canToggle(selectedControl))
              ? 'app-modal-footer--split'
              : ''
          }
        >
          {isEditMode && selectedControl && selectedControl.is_active !== 1 && controlRegisterService.canDelete(selectedControl) && (
            <Button
              variant="delete-outline"
              icon={Trash2}
              type="button"
              onClick={handleDelete}
              disabled={deleting || submitting || submittingForApproval || toggling}
              data-testid="BTN_control_register_delete"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          )}
          {isEditMode && selectedControl && controlRegisterService.canToggle(selectedControl) && (
            <Button
              variant={selectedControl.is_active === 1 ? 'inactive-outline' : 'active-outline'}
              icon={selectedControl.is_active === 1 ? CircleMinus : CircleCheck}
              type="button"
              onClick={handleToggleStatus}
              disabled={toggling || submitting || deleting}
              data-testid="BTN_control_register_toggle_active"
            >
              {toggling ? '...' : selectedControl.is_active === 1 ? 'Inactive' : 'Active'}
            </Button>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            {isEditMode && selectedControl && controlRegisterService.canSubmit(selectedControl) && (
              <Button
                variant="outline"
                type="button"
                onClick={handleSubmitForApproval}
                disabled={submittingForApproval || submitting || deleting}
                data-testid="BTN_control_register_submit_for_approval"
              >
                {submittingForApproval ? 'Submitting...' : 'Submit for Approval'}
              </Button>
            )}
            {isEditMode && selectedControl?.status === 'PENDING_APPROVAL' && (
              <Button
                variant="delete-outline"
                type="button"
                onClick={handleRejectClick}
                disabled={approving || rejecting}
                data-testid="BTN_control_register_reject"
              >
                Reject
              </Button>
            )}
            {isEditMode && selectedControl?.status === 'PENDING_APPROVAL' && (
              <Button
                variant="update"
                icon={Check}
                type="button"
                onClick={handleApprove}
                disabled={approving || rejecting}
                data-testid="BTN_control_register_approve"
              >
                {approving ? 'Approving...' : 'Approve'}
              </Button>
            )}
            {!isEditMode && (
              <WorkflowSaveBar
                canDraft={canModify}
                canSubmit={canModify}
                canApprove={canApproveExtent}
                busy={submitting}
                onSave={handleCreateSave}
              />
            )}
            {isEditMode && canEdit && (
              <Button
                type="submit"
                form="control-form"
                variant="update"
                icon={Check}
                disabled={submitting || deleting || toggling || submittingForApproval}
                data-testid="BTN_control_register_submit"
              >
                {submitting ? 'Updating...' : 'Update'}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      {/* Rejection Modal */}
      <Modal isOpen={showRejectionModal} onClose={() => setShowRejectionModal(false)} size="sm">
        <Modal.Header>
          {selectedControl
            ? <>Reject "<span className="item-highlight">{selectedControl.control_name}</span>"</>
            : 'Reject Control'}
        </Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-600 mb-4">Please provide a reason for rejection:</p>
          <ValidatedTextarea
            label="Rejection Reason"
            required
            name="rejection_reason"
            code={110}
            vh={fieldVhReject}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={4}
            data-testid="INP_control_register_rejection_reason"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="fill"
            icon={Check}
            type="button"
            onClick={handleRejectConfirm}
            disabled={!rejectionReason || rejectionReason.trim() === '' || rejecting}
            data-testid="BTN_control_register_reject_confirm"
          >
            {rejecting ? 'Rejecting...' : 'Confirm Rejection'}
          </Button>
        </Modal.Footer>
      </Modal>

      {configControl && (
        <ControlConfigModal
          isOpen={!!configControl}
          onClose={() => setConfigControl(null)}
          control={configControl}
          categories={categories}
          onSaved={loadControls}
          canModify={canModify}
          canApprove={canApproveExtent}
        />
      )}

      <WorkflowModal
        isOpen={!!workflowRow}
        onClose={() => setWorkflowRow(null)}
        kind={WF_KIND}
        id={workflowRow?.id ?? null}
        onChanged={loadControls}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default ControlRegisterTab;
